<template>
  <div class="flex gap-2">
    <i-date-select
      :locale="locale"
      :mode="mode"
      :modelValue="modelValue"
      @update:modelValue="(d) => $emit('update:modelValue', d)"
    />
    <i-button variant="simple" ref="trigger" @click.prevent="fp.open()">
      <i-icon icon="calendar-alt" size="medium" />
    </i-button>
  </div>
</template>

<script>
import { nextTick } from 'vue';
import * as focusTrap from 'focus-trap';
import flatpickr from 'flatpickr/dist/flatpickr';
import { english as localeEn } from 'flatpickr/dist/l10n/default';
import { German as localeDe } from 'flatpickr/dist/l10n/de';
import 'flatpickr/dist/flatpickr.css';
import IDateSelect from './IDateSelect.vue';

const locales = {
  en: localeEn,
  de: localeDe,
};

/**
 * @requires ./IDateSelect.vue
 */
export default {
  name: 'i-date-flatpickr',
  components: {
    IDateSelect,
  },
  props: {
    locale: {
      type: String,
      default: 'en',
      validator: (l) => ['en', 'de'].includes(l),
    },
    mode: {
      type: String,
      default: 'dateTime',
      validator: (m) => ['date', 'dateTime'].includes(m),
    },
    modelValue: {
      default: null,
    },
  },
  data() {
    return {
      fp: null,
    };
  },
  mounted() {
    this.mountFlatpickr();
  },
  unmounted() {
    this.unmountFlatpickr();
  },
  computed: {
    configEnableTime() {
      return this.mode === 'dateTime';
    },
  },
  watch: {
    configEnableTime() {
      // the `enableTime` config option cannot be updated via flatpickr.set()
      // so we need to rebuild the instance
      this.unmountFlatpickr();
      this.mountFlatpickr();
    },
    modelValue(d) {
      this.fp?.setDate(d);
    },
    locale(l) {
      this.fp?.set('locale', locales[l] ?? locales.en);
    },
  },
  methods: {
    mountFlatpickr() {
      const { trigger } = this.$refs;
      if (!trigger) return;

      let trap = null;
      const { $emit } = this;
      // https://flatpickr.js.org/instance-methods-properties-elements/
      // https://flatpickr.js.org/options/
      this.fp = flatpickr(trigger.$el, {
        allowInput: false,
        enableTime: this.configEnableTime,
        time_24hr: true,
        clickOpens: false, // prevents default flatpickr open-on-focus behavior
        // Events: https://flatpickr.js.org/events/
        onOpen(_, __, fp) { // eslint-disable-line
          if (!trap) {
            // flatpickr does not contain tabbable nodes, se we use the `fallbackFocus` option
            // See: https://github.com/focus-trap/focus-trap?tab=readme-ov-file#your-trap-should-include-a-tabbable-element-or-a-focusable-container
            trap = focusTrap.createFocusTrap(fp.calendarContainer, { fallbackFocus: fp.calendarContainer });
          }
          // Activating the flatpickr focus trap will pause other active traps
          // See: https://github.com/focus-trap/focus-trap?tab=readme-ov-file#one-at-a-time
          trap.activate();
          nextTick(() => {
            // Fix focus and keyboard navigation:
            const selected = fp.calendarContainer.querySelector('.flatpickr-day.selected');
            const today = fp.calendarContainer.querySelector('.flatpickr-day.today');
            const focusTarget = selected || today;
            if (focusTarget) {
              focusTarget.focus();
            }
          });
        },
        onClose() {
          // The first trap is unpaused and again traps focus when the second is deactivated:
          // See: https://github.com/focus-trap/focus-trap?tab=readme-ov-file#one-at-a-time
          trap.deactivate();
          nextTick(() => trigger.$el.focus());
        },
        onChange(dates) {
          const [date] = dates;
          $emit('update:modelValue', date);
        },
        locale: locales[this.locale] ?? locales.en,
      });
      // init date:
      this.fp.setDate(this.modelValue);
    },
    unmountFlatpickr() {
      if (this.fp) {
        this.fp.destroy();
      }
    },
  },
};
</script>

<style scoped>
.i-button.i-simple-btn {
  margin: 0;
  padding: 5px;
}
</style>
